/**
 * Client-side file encryption/decryption using AES-256-GCM.
 *
 * Flow:
 *   Upload: file bytes → AES-256-GCM encrypt → upload encrypted blob
 *           → embed key+iv in E2EE message plaintext as [efile:fileId:mime:name:key:iv]
 *   Download: parse key+iv from plaintext → fetch encrypted blob → AES-256-GCM decrypt
 */

const ALGO = "AES-GCM";
const KEY_LENGTH = 256;

/** Encrypt a file's ArrayBuffer. Returns { encrypted, key, iv } as base64url strings. */
export async function encryptFile(data: ArrayBuffer): Promise<{
  encrypted: ArrayBuffer;
  key: string;
  iv: string;
}> {
  // Generate random AES key and IV
  const key = await crypto.subtle.generateKey(
    { name: ALGO, length: KEY_LENGTH },
    true, // extractable
    ["encrypt", "decrypt"],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM

  // Encrypt
  const encrypted = await crypto.subtle.encrypt(
    { name: ALGO, iv },
    key,
    data,
  );

  // Export key as raw bytes → base64url
  const rawKey = await crypto.subtle.exportKey("raw", key);

  return {
    encrypted,
    key: arrayBufferToBase64url(rawKey),
    iv: arrayBufferToBase64url(iv.buffer),
  };
}

/** Decrypt an encrypted file blob using key and iv (both base64url). */
export async function decryptFile(
  encryptedData: ArrayBuffer,
  keyB64: string,
  ivB64: string,
): Promise<ArrayBuffer> {
  const rawKey = base64urlToArrayBuffer(keyB64);
  const iv = base64urlToArrayBuffer(ivB64);

  const key = await crypto.subtle.importKey(
    "raw",
    rawKey,
    { name: ALGO, length: KEY_LENGTH },
    false,
    ["decrypt"],
  );

  return crypto.subtle.decrypt(
    { name: ALGO, iv: new Uint8Array(iv) },
    key,
    encryptedData,
  );
}

/** Build the file token for E2EE plaintext. */
export function buildEncryptedFileToken(
  fileId: string,
  contentType: string,
  filename: string,
  key: string,
  iv: string,
): string {
  return `[efile:${fileId}:${contentType}:${filename}:${key}:${iv}]`;
}

/** Parse an encrypted file token from plaintext. Returns null if not an efile token. */
export function parseEncryptedFileToken(token: string): {
  fileId: string;
  contentType: string;
  filename: string;
  key: string;
  iv: string;
} | null {
  const match = token.match(/\[efile:([a-f0-9-]+):([^:]+):([^:]+):([A-Za-z0-9_-]+):([A-Za-z0-9_-]+)\]/);
  if (!match) return null;
  return {
    fileId: match[1]!,
    contentType: match[2]!,
    filename: match[3]!,
    key: match[4]!,
    iv: match[5]!,
  };
}

/** Check if a plaintext message contains an encrypted file token */
export function hasEncryptedFile(text: string): boolean {
  return /\[efile:[a-f0-9-]+:[^:]+:[^:]+:[A-Za-z0-9_-]+:[A-Za-z0-9_-]+\]/.test(text);
}

/**
 * Fetch a file from the API (with auth) and optionally decrypt it.
 * Returns a Blob URL ready for use in audio/video/img/link elements.
 */
export async function fetchAndDecryptFile(
  fileId: string,
  mimeType: string,
  encryptionKey?: string,
  encryptionIv?: string,
): Promise<string> {
  const { getAccessToken, ensureValidToken } = await import("./api");
  await ensureValidToken();
  const token = getAccessToken();

  const res = await fetch(`/api/v1/files/${fileId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  let buffer = await res.arrayBuffer();

  // Decrypt if keys present
  if (encryptionKey && encryptionIv) {
    buffer = await decryptFile(buffer, encryptionKey, encryptionIv);
  }

  const blob = new Blob([buffer], { type: mimeType });
  return URL.createObjectURL(blob);
}

/** Wie fetchAndDecryptFile, liefert aber eine self-contained data:-URI (Base64)
 *  statt einer blob:-Object-URL — für eingebettete Exporte. */
export async function fetchAndDecryptToDataUrl(
  fileId: string,
  mimeType: string,
  encryptionKey?: string,
  encryptionIv?: string,
): Promise<string> {
  const { getAccessToken, ensureValidToken } = await import("./api");
  await ensureValidToken();
  const token = getAccessToken();

  const res = await fetch(`/api/v1/files/${fileId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  let buffer = await res.arrayBuffer();
  if (encryptionKey && encryptionIv) {
    buffer = await decryptFile(buffer, encryptionKey, encryptionIv);
  }

  // ArrayBuffer → Base64 (chunked, um Stack-Limits bei großen Dateien zu vermeiden)
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  const base64 = btoa(binary);
  return `data:${mimeType};base64,${base64}`;
}

// ── Base64url helpers ──

function arrayBufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64urlToArrayBuffer(b64: string): ArrayBuffer {
  const padded = b64.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}
